import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Trash2, ArrowLeft } from 'lucide-react';
import { useAuth, useToast } from '@/shared/contexts';
import { Input } from '@/shared/components/ui/Input';
import { authApi } from '@/shared/lib';

const DeleteAccountPage: React.FC = () => {
  const { user, accessToken, logout } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [emailError, setEmailError] = useState('');

  const handleDeleteAccount = async () => {
    // if (!user || !accessToken) return;

    // Validate email
    if (!email || !email.trim()) {
      setEmailError('Email is required');
      notify('error', 'Please enter your email address');
      return;
    }
    setEmailError('');

    setIsDeleting(true);
    try {
      await authApi.post(
        '/api/auth/request-account-deletion',
        { email },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      notify('success', 'Account deletion request submitted');
      logout();
      navigate('/login');
    } catch (err: unknown) {
      console.error('Delete account error:', err);
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      notify('error', message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#006B82] flex items-center justify-center py-8 px-4 sm:px-6">
      <div className="light max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="mb-8 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Delete Your Gio Account
              </h1>
              <p className="text-gray-600">
                You can request deletion of your Gio account and associated
                data.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email used in app
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            required
            className={`bg-white ${emailError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
          />
          {emailError && (
            <p className="mt-1 text-sm text-red-600">{emailError}</p>
          )}
        </div>

        <button
          type="button"
          onClick={handleDeleteAccount}
          disabled={isDeleting}
          className={`px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer ${
            !isDeleting
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isDeleting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            'Request Account Deletion'
          )}
        </button>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-600 mb-2">
            Your account and personal data will be permanently deleted within 7
            days.
          </p>
          <p className="text-gray-600">
            For help:{' '}
            <a
              href="mailto:app@giotheapp.com"
              className="text-[#006B82] hover:underline"
            >
              app@giotheapp.com
            </a>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <Link
            to="/login"
            className="inline-flex items-center text-[#006B82] hover:text-[#00809C] transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
